import { CheckIcon, CloseIcon, Search2Icon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, Text } from "@chakra-ui/react";
import {
  CEPProps,
  getCEPData
} from "components/atendimentos-components/atendimento-form/step-form/tabs-components/digitacao-fields/parts/const";
import { CustomModal } from "components/custom-modal";
import { InputField } from "components/input-field";
import { Toast } from "components/toast";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import { useState } from "react";
import { maskCEPFormat } from "utils/string-formats";
import { ButtonWithLoading } from "../mailing-table/icon-button-with-loading";

interface EditColumnModalParams {
  header: string;
  onConfirm: (value: string) => void;
  onConfirmCEP?: (cepData: CEPProps) => void;
  onCancel?: () => void;
}

const eventName = "openEditColumnModal";

export function EditColumnModal() {
  const [value, setValue] = useState("");
  const [cepData, setCepData] = useState<CEPProps | null>(null);
  const [isCepLoading, setIsCepLoading] = useState(false);

  const { isOpen, onClose, params } = useModalControls<EditColumnModalParams>(
    eventName,
    {
      onOpen: () => {
        setValue("");
        setCepData(null);
      },
      onClose: () => params.onCancel?.()
    }
  );

  const isCEPHeader = params.header === "CEP";

  const handleSearchCEP = async () => {
    const rawCEP = value.replace(/\D/g, "");
    if (rawCEP.length < 8) {
      Toast({ title: "CEP deve ter 8 dígitos", status: "warning" });
      return;
    }
    setIsCepLoading(true);
    try {
      const result = await getCEPData(rawCEP);
      if (result.error) {
        Toast({
          title: result.errorMessage || "CEP não encontrado",
          status: "error"
        });
        setCepData(null);
      } else {
        const { error: _e, errorMessage: _m, ...data } = result;
        setCepData(data as CEPProps);
      }
    } finally {
      setIsCepLoading(false);
    }
  };

  const handleConfirm = () => {
    const rawValue = isCEPHeader ? value.replace(/\D/g, "") : value;
    if (isCEPHeader && cepData) {
      params.onConfirmCEP?.(cepData);
    }
    params.onConfirm?.(rawValue);
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Editar valores em série"
      size="xl"
      closeOnOverlayClick={false}
      modalFooter={
        <Flex gap="8px">
          <Button
            variant="outline"
            onClick={onClose}
            leftIcon={<CloseIcon w="10px" h="10px" />}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} leftIcon={<CheckIcon />}>
            Aplicar
          </Button>
        </Flex>
      }
    >
      <Text fontSize="sm" mb="3">
        Digite o novo valor para a coluna <strong>"{params.header}"</strong>.
        <br />
        Esse valor será aplicado em todas as linhas.
      </Text>

      {isCEPHeader ? (
        <>
          <Grid templateColumns="1fr auto" gap="8px">
            <Flex alignItems="center" gap="8px">
              <Text fontWeight="medium">CEP</Text>
              <InputField
                value={maskCEPFormat(value).value}
                onChange={(e) => {
                  setValue(maskCEPFormat(e.target.value).raw);
                  setCepData(null);
                }}
                inputProps={{
                  onKeyDown: (e) => {
                    if (e.key === "Enter") handleSearchCEP();
                  }
                }}
              />
            </Flex>
            <ButtonWithLoading
              isLoading={isCepLoading}
              isDisabled={value.replace(/\D/g, "").length < 8}
              rightIcon={<Search2Icon />}
              onClick={handleSearchCEP}
              mb="1px"
            >
              Buscar
            </ButtonWithLoading>
          </Grid>

          {cepData && (
            <Box
              mt="12px"
              p="10px"
              bg="green.50"
              borderRadius="md"
              fontSize="sm"
            >
              <Text>
                <strong>Endereço:</strong> {cepData.logradouro}
              </Text>
              <Text>
                <strong>Bairro:</strong> {cepData.bairro}
              </Text>
              <Text>
                <strong>Cidade:</strong> {cepData.localidade}
              </Text>
              <Text>
                <strong>UF:</strong> {cepData.uf}
              </Text>
            </Box>
          )}
        </>
      ) : (
        <InputField
          title="Novo valor"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{
            onKeyDown: (e) => {
              if (e.key === "Enter") handleConfirm();
            }
          }}
        />
      )}
    </CustomModal>
  );
}

export const openEditColumnModal =
  createModalTrigger<EditColumnModalParams>(eventName);
